import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import {
    BlobType,
    DatasetFileTypeOption,
    FileAction,
    FileColumnKey,
    FileModel,
    Folder,
    ItemsToBeRemovedOptions,
    ListSearch,
    MEDFileColumnKeys,
    MessagingCallbackAction,
    MlModel,
    UploadFileItem,
} from '@ca/core/models';
import { CziValidatorService, FileUploadService, Message, MessagingService, UserNotificationService } from '@ca/core/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Guid } from 'guid-typescript';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';
import * as FileSearchSortUtils from '../../../../../utils/file-search-sort/file-search-sort-utils';
import * as FileUtils from '../../../../../utils/file/file-utils';
import { CZI_CONVERSION_RETRY } from '../../../constants/czi-conversion-messages';

@UntilDestroy()
@Component({
    selector: 'ca-selected-files',
    templateUrl: './selected-files.component.html',
    styleUrls: ['./selected-files.component.scss'],
})
export class SelectedFilesComponent implements AfterViewInit {
    @Input() set selectedFiles(selectedFiles: FileModel[]) {
        this._selectedFiles = selectedFiles;
        this.isCziConversionInProgress = selectedFiles.some((file: FileModel): boolean => file.convertStatus === 'Active');
        this.isValidationInProgress = selectedFiles.some((file: FileModel): boolean => file.validationStatus === 'PendingValidation');
        this.numberOfConversionFailedFiles = this.cziConverterService.getNumberOfConversionFailedFiles(selectedFiles);

        this.isCziConversionInProgressChange.emit(this.isCziConversionInProgress);
        this.isValidationInProgressChange.emit(this.isValidationInProgress);

        this.failedConversionFiles = selectedFiles.filter(file => FileUtils.hasFailedConversion(file.convertStatus));

        if (this.datasetFileType !== DatasetFileTypeOption.ANNOTATIONS) {
            this.invalidFiles = this.datasetId ? selectedFiles.filter(file => FileUtils.hasInvalidValidation(file.validationStatus)) : [];
        } else {
            this.invalidFiles = selectedFiles.filter(file => FileUtils.hasInvalidValidation(file.validationStatus)) || [];
        }
        this.unknownFiles = selectedFiles.filter(file => file.validationStatus && FileUtils.hasUnknownValidationStatus(file.validationStatus)) || [];

        // keep one instance of each duplicate (i.e., if a file appears in both failedConversionFiles and invalidFiles)
        const fileMap = new Map<string, FileModel>();

        [...this.failedConversionFiles, ...this.invalidFiles].forEach(file => {
            if (!fileMap.has(file.id)) {
                fileMap.set(file.id, file);
            }
        });
        // used to display the correct number of added files
        this.invalidFilesNoDuplicates = Array.from(fileMap.values());

        // Revalidation should only be possible if the files' validationStatus is Pending, Invalid, Unknown or null at the start
        if (!this.arePendingFilesSetTheFirstTime) {
            this.arePendingFilesSetTheFirstTime = true;
            this.pendingFilesToValidate = this.datasetId
                ? selectedFiles.filter(file => file.validationStatus && FileUtils.hasPendingValidation(file.validationStatus))
                : [];
        }
        this.prepareDisplayFiles();
    }

    get selectedFiles(): FileModel[] {
        return this._selectedFiles;
    }

    private arePendingFilesSetTheFirstTime = false;

    get retryMessagingAction(): MessagingCallbackAction<void> {
        return { topicId: this.messagingTopicId, action: CZI_CONVERSION_RETRY };
    }
    @Input() showViewAllButton = true;
    @Input() showPreviewMode = false;
    @Input() showViewIconForSelectedItems = true;
    @Input() showFileSize = true;
    @Input() filesSelectable = false;
    @Input() targetFolderToBeCreated: string;
    @Input() currentFolderid: string;
    @Input() uploaderKey: string;
    @Input() operationId: string;
    @Input() isFileExplorerShown: string;
    @Input() supportedFileTypes = new Array<string>();
    @Input() isMedCZIConversionDisabled: boolean = false;

    @Input() datasetId?: string;
    @Input() datasetFileType?: DatasetFileTypeOption;
    @Input() isResetButtonShown?: boolean;
    @Input() isSelectedFilesShown?: boolean;
    @Input() isUploadOfFilesEnabled?: boolean;
    @Input() columns: FileColumn<FileColumnKey | MEDFileColumnKeys>[] = [
        { key: 'fileStatus' },
        {
            key: 'sizeInBytes',
            translationKey: 'GENERAL.COLUMN.SIZE',
            defaultValue: '\u2014',
            transform: 'fileSize',
        },
    ];
    @Input() actions: FileAction[] = ['remove', 'view'];

    @Input() set searchAndSortOptions(searchAndSortOptions: ListSearch) {
        this.currentSearchOptions = searchAndSortOptions == null ? this.defaultSearchOptions : searchAndSortOptions;
        this.prepareDisplayFiles();
    }
    @Input() isSegmentation?: boolean;
    @Input() selectedModel?: MlModel | null = null;

    @Output() changeSearchAndSortOptions = new EventEmitter<ListSearch>();
    @Output() selectedFilesUpdated = new EventEmitter<FileModel[]>();
    @Output() targetFolderCreated = new EventEmitter<Folder>();
    @Output() filesAdded = new EventEmitter<UploadFileItem[]>();
    @Output() removeItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel>>();
    @Output() changeStudyId: EventEmitter<{ studyId: string; fileIDs: string[] }> = new EventEmitter();
    @Output() changeCustomTags: EventEmitter<{ customTags: string[]; fileIDs: string[] }> = new EventEmitter();
    @Output() removeTag: EventEmitter<{ customTags: string[]; fileIDs: string[] }> = new EventEmitter();
    @Output() isCziConversionInProgressChange = new EventEmitter<boolean>();
    @Output() isValidationInProgressChange = new EventEmitter<boolean>();

    blobType = BlobType.UserFile;
    isCziConversionAboutToStart = false;
    isCziConversionInProgress = false;
    isValidationInProgress = false;
    numberOfConversionFailedFiles = 0;
    selectedIndex: number | null = null;
    pagedSelectedFiles: FileModel[] = [];
    numberOfSelectedFiles: number = 0;
    numberOfUnprocessedFiles: number = 0;
    invalidFiles: FileModel[] = [];
    haveAllFilesFailedConversion: boolean;
    unknownFiles: FileModel[] = [];
    invalidFilesNoDuplicates: FileModel[] = [];
    failedConversionFiles: FileModel[];
    pendingFilesToValidate: FileModel[] = [];
    pageSize = 10;
    defaultSearchOptions: ListSearch = {
        pageNumber: 1,
        pageSize: this.pageSize,
        searchQuery: '',
        sortBy: '',
    };
    currentSearchOptions = this.defaultSearchOptions;
    initialValueOfFilesToBeUploaded$ = this.fileUploadService.initialValueOfFilesToBeUploaded$;
    cancelUpload: FileItem;
    cancelAllUploads: { cancel: boolean };
    uploader: FileUploader | null;
    initialUploaderQueueLength = 0;
    uniqueFiles: FileModel[] = [];

    get isSelectedFilesInfoVisible(): boolean {
        return (
            this.selectedFiles.length > 0 &&
            (this.uploader?.queue?.length || 0) === 0 &&
            !this.isCziConversionAboutToStart &&
            !this.isCziConversionInProgress &&
            !this.isValidationInProgress
        );
    }

    get showFileStatus(): boolean {
        return this.datasetFileType !== DatasetFileTypeOption.ANNOTATIONS;
    }

    get numberOfCziFilesBeingConverted(): number {
        return this.cziConverterService.getNumberOfFilesBeingConverted(this.selectedFiles);
    }

    get numberOfFilesBeingValidated(): number {
        return this.selectedFiles.filter(file => file.validationStatus === 'PendingValidation' || file.validationStatus === 'Unknown').length;
    }

    private messagingTopicId = Guid.create().toString();
    private _selectedFiles: FileModel[] = [];

    constructor(
        private cziConverterService: CziConverterService,
        private cziValidatorService: CziValidatorService,
        private fileUploadService: FileUploadService,
        private messagingService: MessagingService,
        private userNotificationService: UserNotificationService,
    ) {
        this.messagingService
            .subscription(this.messagingTopicId)
            .pipe(
                tap(
                    ({
                        action,
                        payload: { operationId, files } = {} as { operationId: string; files: FileModel[] },
                    }: Message<{ operationId: string; files: FileModel[] }>): void => {
                        if (action === CZI_CONVERSION_RETRY) {
                            this.isCziConversionAboutToStart = true;
                            files.forEach((file: FileModel): void => {
                                this.cziConverterService
                                    .convertFileToCziIfNotCziAndStartPolling$(
                                        operationId,
                                        this.selectedFiles,
                                        file,
                                        undefined,
                                        undefined,
                                        this.isMedCZIConversionDisabled,
                                    )
                                    .pipe(
                                        tap((updatedFiles: FileModel[]): void => {
                                            this.isCziConversionAboutToStart = false;
                                            this.selectedFiles = updatedFiles;
                                        }),
                                        untilDestroyed(this),
                                    )
                                    .subscribe();
                            });
                        }
                    },
                ),
                untilDestroyed(this),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        // setTimeout to avoid expression has changed after it was checked exception
        setTimeout((): void => {
            this.uploader = this.fileUploadService.fetchFromCache(this.uploaderKey);
            // fetch the initial files length from service to correctly calculate upload progress in upload progress container
            this.fileUploadService.loadInitialValueOfFiles();
            this.initialUploaderQueueLength = this.uploader?.queue?.length || 0;
        });
    }

    onFileSearchOrSort(params: ListSearch): void {
        this.searchAndSortOptions = params;
        this.changeSearchAndSortOptions.emit(params);
    }

    onUploadCompleted(file: FileModel): void {
        this.selectedFiles = [...[file], ...this.selectedFiles];
        // hide spinner when files are displayed in the dataset explorer UI
        this.userNotificationService.hideSpinner();

        if (this.datasetFileType === DatasetFileTypeOption.ANNOTATIONS) {
            this.selectedFilesUpdated.emit([...this.selectedFiles]);
        } else {
            this.isCziConversionAboutToStart = true;
            this.cziConverterService
                .convertFileToCziIfNotCziAndStartPolling$(this.operationId, this.selectedFiles, file, undefined, undefined, this.isMedCZIConversionDisabled)
                .pipe(
                    map((updatedFiles: FileModel[]): FileModel[] => {
                        this.isCziConversionAboutToStart = false;
                        return this.updateSelectedFilesAndEmitThem(updatedFiles);
                    }),
                    switchMap((updatedSelectedFiles: FileModel[]): Observable<FileModel[]> => {
                        if (this.datasetFileType === DatasetFileTypeOption.FILES) {
                            const convertedAndNotValidatedFiles = FileUtils.getConvertedAndNotValidatedFiles(updatedSelectedFiles);
                            if (convertedAndNotValidatedFiles.length > 0) {
                                this.updateSelectedFilesAndEmitThem(
                                    convertedAndNotValidatedFiles.map(file => {
                                        file.validationStatus = 'PendingValidation';
                                        return file;
                                    }),
                                );
                                return this.cziValidatorService.validate$(this.operationId, convertedAndNotValidatedFiles, false);
                            }
                        } else if (this.isSegmentation) {
                            const convertedAndNotValidatedFiles = FileUtils.getConvertedAndNotValidatedFiles(updatedSelectedFiles);
                            if (convertedAndNotValidatedFiles.length > 0) {
                                this.updateSelectedFilesAndEmitThem(
                                    convertedAndNotValidatedFiles.map(file => {
                                        file.validationStatus = 'PendingValidation';
                                        return file;
                                    }),
                                );

                                this.cziValidatorService
                                    .validate$(this.operationId, convertedAndNotValidatedFiles, false, this.isSegmentation, this.selectedModel)
                                    .pipe(
                                        tap(validatedFiles => {
                                            this.updateSelectedFilesAndEmitThem(validatedFiles);
                                        }),
                                        catchError((error: unknown): Observable<[]> => {
                                            console.error('Validation failed:', error);
                                            this.userNotificationService.showError('Segmentation validation failed.');
                                            return of([]);
                                        }),
                                        untilDestroyed(this),
                                    )
                                    .subscribe();
                            }
                        }

                        return of(updatedSelectedFiles);
                    }),
                    tap((updatedFiles: FileModel[]): void => {
                        this.updateSelectedFilesAndEmitThem(updatedFiles);
                    }),
                    untilDestroyed(this),
                )
                .subscribe();
        }
    }

    private updateSelectedFilesAndEmitThem(updatedFiles: FileModel[]): FileModel[] {
        const updatedSelectedFiles = FileUtils.getUpdatedSelectedFiles(this.selectedFiles, updatedFiles);
        this.selectedFiles = [...updatedSelectedFiles];
        this.selectedFilesUpdated.emit([...updatedSelectedFiles]);
        return updatedSelectedFiles;
    }

    onFilesAdded(files: UploadFileItem[]): void {
        this.isCziConversionAboutToStart = false;
        this.initialUploaderQueueLength = this.uploader?.queue?.length || 0;

        // store data to calculate the correct percentage of uploaded files (base must be initial number of files).
        // Calculation is needed to take into account files that user might additionally
        // upload while other files in the uploading process.
        if (this.fileUploadService.isAnyUploadInProgress()) {
            this.fileUploadService.setInitialValueOfFiles(this.fileUploadService.loadInitialValueOfFiles() + files.length);
        } else {
            this.fileUploadService.setInitialValueOfFiles(this.initialUploaderQueueLength);
        }

        // show spinner to prevent the user from closing the modal too quickly, thus preventing the files to be displayed in the dataset explorer UI
        this.userNotificationService.showSpinner();
        this.filesAdded.emit(files);
    }

    onUploadCancelled(): void {
        this.initialUploaderQueueLength = this.uploader?.queue?.length || 0;
    }

    onRemoveItems(removeFilesOptions: ItemsToBeRemovedOptions<FileModel>): void {
        this.removeItems.emit(removeFilesOptions);
    }

    onCancelFileUpload(file: FileItem): void {
        this.fileUploadService.setInitialValueOfFiles(this.fileUploadService.loadInitialValueOfFiles() - 1);
        this.cancelUpload = file;
    }

    onCancelAllFileUploads(): void {
        this.cancelAllUploads = { cancel: true };
    }

    onChangeStudyID(event: { studyId: string; fileIDs: string[] }): void {
        this.changeStudyId.emit(event);
    }

    onChangeCustomTags(event: { customTags: string[]; fileIDs: string[] }): void {
        this.changeCustomTags.emit(event);
    }

    onTagRemoved(event: { customTags: string[]; fileIDs: string[] }): void {
        this.removeTag.emit(event);
    }

    reValidate(): void {
        if (this.datasetFileType === DatasetFileTypeOption.ANNOTATIONS) {
            return;
        }

        if (this.isSegmentation) {
            const filesToValidate = [...this.pendingFilesToValidate, ...this.unknownFiles, ...this.invalidFiles];
            this.updateSelectedFilesAndEmitThem(
                filesToValidate.map(file => {
                    file.validationStatus = 'PendingValidation';
                    return file;
                }),
            );
            this.cziValidatorService
                .validate$(this.operationId, filesToValidate, true, this.isSegmentation, this.selectedModel)
                .pipe(
                    tap(validatedFiles => {
                        this.updateSelectedFilesAndEmitThem(validatedFiles);
                        this.pendingFilesToValidate = [];
                        this.unknownFiles = [];
                        this.invalidFiles = [];
                    }),
                    untilDestroyed(this),
                )
                .subscribe();
        } else {
            const filesToValidate = [...this.pendingFilesToValidate, ...this.unknownFiles, ...this.invalidFiles];
            this.updateSelectedFilesAndEmitThem(
                filesToValidate.map(file => {
                    file.validationStatus = 'PendingValidation';
                    return file;
                }),
            );
            this.cziValidatorService
                .validate$(this.datasetId!, filesToValidate, true)
                .pipe(
                    tap(updatedFiles => {
                        this.updateSelectedFilesAndEmitThem(updatedFiles);
                        this.pendingFilesToValidate = [];
                        this.unknownFiles = [];
                        this.invalidFiles = [];
                    }),
                    untilDestroyed(this),
                )
                .subscribe();
        }
    }

    private prepareDisplayFiles(): void {
        this.numberOfUnprocessedFiles = this.selectedFiles.filter(file => file.validationStatus != null && file.convertStatus != null).length;
        this.numberOfSelectedFiles = this.selectedFiles.length;
        this.pagedSelectedFiles = FileSearchSortUtils.getCurrentPageOfFiles(this.selectedFiles, this.currentSearchOptions);
    }
}
